/**
 * サイトワイド監修者情報
 *
 * 2026-05-28 E-E-A-T 強化施策。
 * 全レッスン (lessons) と全ガイド (articles / guides) の author/supervisor として
 * Person 型 schema.org マークアップを出力するために利用する。
 *
 * 単一監修者運用のため、DB の lessons.supervisor_id カラムは追加せず
 * ここで一括管理する。複数監修者運用が必要になった段階で
 * article_creators (= 監修者マスター) + 中間テーブル参照に切り替える。
 *
 * DB 上の正本は article_creators テーブル (slug=ikuta-rikuto)。
 */

export const SITE_SUPERVISOR = {
  slug: "ikuta-rikuto",
  name: "生田 陸人",
  /** schema.org Person.jobTitle 用 */
  jobTitle: "ゆめさくエンジニア / 大手 IT 企業 現役ソフトウェアエンジニア",
  /** UI 表示用の短縮タイトル */
  shortTitle: "ゆめさくエンジニア / 現役ソフトウェアエンジニア",
  /** 著者プロフィールページ URL (canonical) */
  profileUrl: "https://yumesaku.tech/authors/ikuta-rikuto",
  /** /creators 配下の URL (alias) */
  creatorUrl: "https://yumesaku.tech/creators/ikuta-rikuto",
  /**
   * schema.org Person.image
   * 実写真。/avatars/ikuta-rikuto.png に配置 (266x252 PNG)。
   */
  imageUrl: "https://yumesaku.tech/avatars/ikuta-rikuto.png" as string | null,
  /** Person.knowsAbout — 専門領域 */
  knowsAbout: [
    "Ruby on Rails",
    "Nuxt.js",
    "Next.js",
    "CakePHP",
    "Laravel",
    "Go",
    "Express",
    "AI プロンプトチューニング",
    "BtoB SaaS",
  ] as string[],
  /** schema.org Person.sameAs (LinkedIn / X など。空でも可) */
  sameAs: [] as string[],
} as const;

/**
 * schema.org Person オブジェクトを返す。
 * Article / TechArticle / LearningResource の author に差し込む共通ロジック。
 */
export function getSupervisorPersonSchema() {
  const schema: Record<string, unknown> = {
    "@type": "Person",
    name: SITE_SUPERVISOR.name,
    url: SITE_SUPERVISOR.profileUrl,
    jobTitle: SITE_SUPERVISOR.jobTitle,
    knowsAbout: [...SITE_SUPERVISOR.knowsAbout],
  };

  if (SITE_SUPERVISOR.imageUrl) {
    schema.image = SITE_SUPERVISOR.imageUrl;
  }
  if (SITE_SUPERVISOR.sameAs.length > 0) {
    schema.sameAs = [...SITE_SUPERVISOR.sameAs];
  }

  return schema;
}

/**
 * 編集部 (Organization) author schema
 */
export const EDITORIAL_TEAM = {
  name: "ゆめさく",
  url: "https://yumesaku.tech",
} as const;

/** 発行者と著者の Organization。JSON-LD はどこでもこの 1 つを @id で参照する */
export const ORGANIZATION_ID = "https://yumesaku.tech/#organization";
export const ORGANIZATION_NAME = "ゆめさく";

export function organizationRef() {
  return {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    url: "https://yumesaku.tech",
  };
}

export function getEditorialTeamOrgSchema() {
  return organizationRef();
}

/**
 * author に「ゆめさく編集部 + 生田陸人」を併記した schema (配列形式)
 * Google Rich Results は author 配列を受け入れる、Person を含むことで
 * E-E-A-T が個人として評価される。
 */
export function getCombinedAuthorSchema() {
  return [getEditorialTeamOrgSchema(), getSupervisorPersonSchema()];
}
